import React, { useCallback, useMemo } from 'react'
import { QuestionnaireServicePromiseClient } from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_grpc_web_pb'
import {
  DeleteQuestionnaireRequest,
  GetKnowledgeBasesResponse,
  UploadKnowledgeBaseRequest,
} from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import log from 'loglevel'
import { ContentStorePromiseClient } from '@trustero/trustero-api-web/lib/contentstore/contentstore_grpc_web_pb'
import { ContentID } from '@trustero/trustero-api-web/lib/contentstore/contentstore_pb'
import { useFetchUrlBodies } from 'src/components/async/document/useDocument'
import { OwnerAssignmentButton } from 'src/components/Reusable/Buttons'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { UploadKB } from './knowledgeBase.constants'
import { KnowledgeBaseApi } from './knowledgeBaseTab.types'
import { getKnowledgeBaseSource } from './modals/KnowledgeBaseModals.helpers'

export const useGetKnowledgeBases = (
  preventFetch?: boolean,
): GrpcResponse<GetKnowledgeBasesResponse> => {
  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.getKnowledgeBases,
    new Empty(),
    !preventFetch,
  )

  return NewGrpcResponse(response)
}

export const useDeleteKnowledgeBase = (): ((id: string) => Promise<void>) => {
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )

  return async (id: string) => {
    const req = new DeleteQuestionnaireRequest().setId(id)
    await questionnaireClient.deleteKnowledgeBase(req)
  }
}

export const useUploadKnowledgeBase = (): (({
  fileName,
  file,
  mime,
}: UploadKB) => Promise<void>) => {
  const questionnaireClient = useAuthorizedGrpcClient(
    QuestionnaireServicePromiseClient,
  )

  return async ({ fileName, file, mime }: UploadKB) => {
    const request = new UploadKnowledgeBaseRequest()
    request.setFileName(fileName)
    request.setFile(file)
    request.setMime(mime)

    await questionnaireClient.uploadKnowledgeBase(request)
  }
}

// useKnowledgeBaseApi is the one implementation of the KnowledgeBaseApi
// contract KnowledgeBaseTab renders against, on both the GRC Questions page
// and the Vendor Portal. A vendor-only account is a SAAS_BUYER, and
// auth.constants.go's saasBuyerRoutes already admits these three
// QuestionnaireService RPCs plus ContentStore/GetDownloadURL, so the vendor's
// JWT reaches them and every handler scopes to its own account.
export const useKnowledgeBaseApi = (): KnowledgeBaseApi => {
  const { data, loading, mutate } = useGetKnowledgeBases()
  const uploadFunc = useUploadKnowledgeBase()
  const deleteFunc = useDeleteKnowledgeBase()
  // Downloads go through the content store rather than the questionnaire
  // service: getDownloadURL for the row's content id, then fetch the body with
  // the same URL fetcher useFetchDocumentBody(ies) use. Called directly (not
  // via useFetchDocumentBody) because that hook binds one content id at hook
  // time, and this API downloads whichever row the user picked.
  const contentStoreClient = useAuthorizedGrpcClient(ContentStorePromiseClient)
  const getUrlBodies = useFetchUrlBodies()

  const knowledgeBases = useMemo(
    () => data?.getKnowledgeBasesList() ?? [],
    [data],
  )

  const rows = useMemo(
    () =>
      knowledgeBases.map((kb) => ({
        id: kb.getId(),
        fileName: kb.getFileName(),
        createdAtMs: kb.getCreatedAt()?.toDate().getTime() ?? 0,
        source: getKnowledgeBaseSource(kb.getSource()),
        userCell: (
          <OwnerAssignmentButton
            modelId={kb.getId()}
            modelType={MODEL_TYPE.QUESTIONNAIRE}
            email={kb.getUserEmail()}
            model={undefined}
            isDisabled
          />
        ),
      })),
    [knowledgeBases],
  )

  const contentByKbId = useMemo(
    () =>
      new Map(
        knowledgeBases.map((kb) => [
          kb.getId(),
          { contentId: kb.getContentId(), fileName: kb.getFileName() },
        ]),
      ),
    [knowledgeBases],
  )

  const refetch = useCallback(() => {
    mutate()
  }, [mutate])

  const upload = useCallback(
    async (
      fileName: string,
      file: Uint8Array,
      mime: string,
    ): Promise<boolean> => {
      try {
        await uploadFunc({ fileName, file, mime })
        return true
      } catch (err) {
        log.error('error when uploading knowledge base: ', err)
        return false
      }
    },
    [uploadFunc],
  )

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await deleteFunc(id)
        return true
      } catch (err) {
        log.error(`error when deleting knowledge base ${id}: `, err)
        return false
      }
    },
    [deleteFunc],
  )

  const download = useCallback(
    async (id: string) => {
      const content = contentByKbId.get(id)
      if (!content?.contentId) return undefined
      try {
        const url = await contentStoreClient.getDownloadURL(
          new ContentID().setContentId(content.contentId),
        )
        const [body] = await getUrlBodies([url])
        if (!body || typeof body === 'string') return undefined
        return { fileName: content.fileName, file: body }
      } catch (err) {
        log.error(`error downloading knowledge base ${id}: `, err)
        return undefined
      }
    },
    [contentByKbId, contentStoreClient, getUrlBodies],
  )

  return { rows, loading, refetch, upload, remove, download }
}
